import type { ProjectState } from './projectTypes';

const AUTOSAVE_KEY = 'osal.autosave.v1';

export type AutosaveReadResult =
  | { readonly status: 'empty' }
  | { readonly status: 'loaded'; readonly state: ProjectState }
  | { readonly status: 'invalid'; readonly message: string };

function validateProjectState(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null) return 'Der gespeicherte Projektzustand ist kein Objekt.';
  const record = value as Record<string, unknown>;
  const patterns = record['patterns'];
  const activePatternId = record['activePatternId'];
  if (!Array.isArray(patterns) || patterns.length === 0 || typeof activePatternId !== 'string') {
    return 'Die gespeicherte Pattern-Bank hat keine gueltige Projektstruktur.';
  }

  const patternIds = new Set<string>();
  for (let index = 0; index < patterns.length; index += 1) {
    const pattern = patterns[index];
    if (typeof pattern !== 'object' || pattern === null || Array.isArray(pattern)) {
      return `Gespeichertes Pattern ${index + 1} hat keine gueltige Struktur.`;
    }
    const id = (pattern as Record<string, unknown>)['id'];
    if (typeof id !== 'string' || id.length === 0) {
      return `Gespeichertes Pattern ${index + 1} hat keine gueltige ID.`;
    }
    if (patternIds.has(id)) {
      return `Pattern-ID "${id}" ist in der gespeicherten Bank doppelt vorhanden.`;
    }
    patternIds.add(id);
  }

  if (!patternIds.has(activePatternId)) {
    return 'Die aktive Pattern-ID verweist auf kein gespeichertes Pattern.';
  }
  return undefined;
}

/** Liest den Autosave mit unterscheidbarem Leer-, Erfolgs- oder Fehlerstatus. */
export function readAutosave(): AutosaveReadResult {
  try {
    const raw = window.localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return { status: 'empty' };
    const parsed: unknown = JSON.parse(raw);
    const validationError = validateProjectState(parsed);
    if (validationError) return { status: 'invalid', message: validationError };
    return { status: 'loaded', state: parsed as ProjectState };
  } catch {
    return { status: 'invalid', message: 'Der gespeicherte Projektzustand konnte nicht gelesen werden.' };
  }
}

/** Best-effort: liest den zuletzt automatisch gespeicherten Projektstand. Nie werfend. */
export function loadAutosave(): ProjectState | undefined {
  const result = readAutosave();
  return result.status === 'loaded' ? result.state : undefined;
}

/** Best-effort: speichert den Projektstand lokal. Fehler (z.B. privater Modus) werden ignoriert. */
export function saveAutosave(state: ProjectState): void {
  try {
    window.localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(state));
  } catch {
    // Autosave ist eine Komfortfunktion, kein garantierter Speicherpfad.
  }
}
